import React, { useState, useContext, useMemo } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";
import { CustomSelectDropdown } from "../../../atoms/Inputs";

import { demoContext } from "../../../../shared/store/contexts";

import * as Icon from "react-feather";
import ActionPageHero from "../../../molecules/ActionPageHero";
import { P, H2 } from "../../../atoms/Texts";
import {
  DarkFormField,
  DarkTextArea,
  MultipleFileInput,
} from "../../../atoms/Inputs";
import { PrimaryButton } from "../../../atoms/Buttons";
import InitiativeSuccessModal from "../../../organisms/InitiativeSuccessModal";

import { ErrorMessage } from "@hookform/error-message";
import { selectStyle } from "../../../../shared/fixtures/select_style";
import { toastWarning } from "../../../../shared/helpers";

const CreateInitiative = () => {
  const [openSuccess, setOpenSuccess] = useState(false);
  const { services: data } = useContext(demoContext);
  const services = useMemo(() => {
    return data.map((service) => ({
      label: service.name,
      value: service.id,
    }));
  }, [data]);

  const { id } = useParams();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors: formStateErrors },
  } = useForm();

  const [files, setFiles] = React.useState([]);

  const submitForm = handleSubmit(async () => {
    toastWarning("Cannot perform this action on Demo mode");
  });

  return (
    <main className="flex flex-col w-full">
      <ActionPageHero
        className="organization-container"
        title="Initiatives"
        description="You manage the initiative you create by inviting other organizations to the initiative"
        pClassName="lg:w-4/5"
        action
        actionComponent={null}
      />

      <div className="organization-container pb-10 flex-grow flex flex-col mt-6">
        <div>
          <Link to={`/dashboard/organization/${id}/initiatives`}>
            <div className="cursor-pointer flex items-center mb-4">
              <Icon.ChevronLeft size={14} color="#7D7B96" className="mr-2" />
              <P color="#7D7B96" fontSize="14px">
                Back to Initiatives
              </P>
            </div>
          </Link>

          <section className="xl:w-1/2">
            <H2 fontSize="26px" color="#22202D" className="">
              Initiative Request
            </H2>
            <P color="#7D7B96" fontSize="14px">
              Complete the information in this form to request for an initiative.
              Please note that the initiative request is a paid feature
            </P>

            <form onSubmit={submitForm} className=" mt-6 grid grid-cols-1 gap-4">
              <div key={`name`} className="col-span-2">
                <div className="flex items-center justify-between">
                  <DarkFormField
                    containerClass="w-full"
                    type="text"
                    id={`name`}
                    name={`name`}
                    placeholder="Input initiative name"
                    label={
                      <>
                        <span>Name of Initiative </span>
                        <span
                          className=""
                          style={{
                            color: "#E62250",
                          }}
                        >
                          *
                        </span>
                      </>
                    }
                    {...register("name", {
                      required: "Please enter initiative name.",
                    })}
                  />
                </div>

                <ErrorMessage
                  errors={formStateErrors}
                  name={`name`}
                  as={
                    <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                  }
                >
                  {({ messages }) =>
                    messages &&
                    Object.entries(messages).map(([type, message]) => (
                      <P key={type}>{message}</P>
                    ))
                  }
                </ErrorMessage>
              </div>
              <div className="mb-4">
                <label htmlFor={`service`}>
                  <P
                    fontSize="14px"
                    fontFamily="semi"
                    color="#47495C"
                    className="mb-2"
                  >
                    Choose a service
                    <span
                      className=""
                      style={{
                        color: "#E62250",
                      }}
                    >
                      *
                    </span>
                  </P>
                </label>

                <Controller
                  control={control}
                  id="service"
                  name="service"
                  render={({ field }) => (
                    <CustomSelectDropdown
                      {...field}
                      styles={selectStyle}
                      autoComplete="true"
                      options={services}
                      placeholder="Choose a service"
                      className="w-full"
                    />
                  )}
                  rules={{
                    required: true,
                    validate: (value) => value?.value?.length > 0,
                  }}
                />
                {formStateErrors.service && (
                  <P fontSize="12px" className="mt-2 mb-2 text-red-400 text-sm">
                    Select a service
                  </P>
                )}
              </div>
              <div key={`description`} className=" col-span-2">
                <div className="flex items-center justify-between">
                  <DarkTextArea
                    rows={3}
                    containerClass="w-full"
                    id={`description`}
                    name={`description`}
                    placeholder="Type description here"
                    label="Description"
                    required
                    {...register("description", {
                      required: "Please enter location's last name.",
                    })}
                  />
                </div>

                <ErrorMessage
                  errors={formStateErrors}
                  name={`description`}
                  as={
                    <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                  }
                >
                  {({ messages }) =>
                    messages &&
                    Object.entries(messages).map(([type, message]) => (
                      <P key={type}>{message}</P>
                    ))
                  }
                </ErrorMessage>
              </div>
              <div key={`images`} className=" col-span-2">
                <label htmlFor={`images`}>
                  <P
                    fontSize="14px"
                    fontFamily="semi"
                    color="#47495C"
                    className="mb-2"
                    fontWeight="bold"
                  >
                    Update Images
                    <span
                      className=""
                      style={{
                        fontWeight: "normal",
                      }}
                    >
                      (optional)
                    </span>
                  </P>
                </label>
                <MultipleFileInput
                  rows={3}
                  label={
                    <div>
                      <P className="font-normal" color="#442ED0">
                        <span className="font-semibold text-sm">
                          Click to upload
                        </span>{" "}
                        {""}
                        <span className="text-gray-500  text-sm">
                          or drag and drop
                        </span>
                      </P>

                      <p className="text-xs ">SVG, PNG, JPG (max. 1MB)</p>
                    </div>
                  }
                  id={`images`}
                  name={`images`}
                  accept=".jpeg,.jpg,.png"
                  className="w-full"
                  files={files}
                  setFiles={setFiles}
                  maxFile="10"
                  {...register("images")}
                />
              </div>
              <div className="w-full bg-red-400">
                <PrimaryButton type="submit" className="w-full">
                  Send Request
                </PrimaryButton>
              </div>
            </form>
          </section>
        </div>
      </div>

      {openSuccess && (
        <InitiativeSuccessModal
          closeModal={() =>
            (window.location.pathname = `/dashboard/organization/${id}/initiatives`)
          }
          message1="Thank you for your request in creating an initiative. Our Team will reach out to you with your registered email address."
          message2="Please NOTE that this request can take 2-3 weeks to be approved or declined. Please do not resend another request."
        />
      )}
    </main>
  );
};

export default CreateInitiative;
